import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components
import FloatingCartButton from './components/FloatingCartButton';
import MobileBottomNav from './components/MobileBottomNav';
import ScrollToTop from './components/ScrollToTop';
import InstallPWA from './components/InstallPWA';
import AdminInstallPWA from './components/AdminInstallPWA';
import AdminPWAConfig from './components/AdminPWAConfig';

const Header = lazy(() => import('./components/Header'));
const Footer = lazy(() => import('./components/Footer'));
const AdminLayout = lazy(() => import('./components/AdminLayout'));

const HomePage = lazy(() => import('./pages/HomePage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));

const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ProductManagement = lazy(() => import('./pages/admin/ProductManagement'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminProductForm = lazy(() => import('./pages/admin/AdminProductForm'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminCoupons = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const ManagementPage = lazy(() => import('./pages/admin/ManagementPage'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const WhatsAppManagement = lazy(() => import('./pages/admin/WhatsAppManagement'));

function RouteLoadingFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
    </div>
  );
}

function PublicRouteLayout({ children }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow bg-gray-50 pb-16 md:pb-0">
        {children}
      </main>
      <Footer />
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <>
      <AdminPWAConfig />
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={true}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <ScrollToTop />

      {!isAdminRoute && <FloatingCartButton />}
      {!isAdminRoute && <MobileBottomNav />}
      {!isAdminRoute && <InstallPWA />}
      <AdminInstallPWA isAdminRoute={isAdminRoute} />

      {!isAdminRoute && (
        <a
          href="https://instagram.com/Iva.center"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-20 left-4 md:bottom-6 md:right-6 z-50 w-14 h-14 flex items-center justify-center bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 hover:from-purple-700 hover:via-pink-600 hover:to-orange-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
          aria-label="تابعنا على انستغرام"
        >
          <svg
            className="w-7 h-7"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping"></span>
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
        </a>
      )}

      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<PublicRouteLayout><HomePage /></PublicRouteLayout>} />
          <Route path="/products" element={<PublicRouteLayout><ProductsPage /></PublicRouteLayout>} />
          <Route path="/product/:id" element={<PublicRouteLayout><ProductDetailPage /></PublicRouteLayout>} />
          <Route path="/cart" element={<PublicRouteLayout><CartPage /></PublicRouteLayout>} />
          <Route path="/checkout" element={<PublicRouteLayout><CheckoutPage /></PublicRouteLayout>} />
          <Route path="/login" element={<PublicRouteLayout><LoginPage /></PublicRouteLayout>} />
          <Route path="/register" element={<PublicRouteLayout><RegisterPage /></PublicRouteLayout>} />
          <Route path="/orders" element={<PublicRouteLayout><OrdersPage /></PublicRouteLayout>} />
          <Route path="/profile" element={<PublicRouteLayout><ProfilePage /></PublicRouteLayout>} />

          {/* Admin Login Route */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
          <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
          <Route path="/admin/products" element={<AdminLayout><ProductManagement /></AdminLayout>} />
          <Route path="/admin/products/old" element={<AdminLayout><AdminProducts /></AdminLayout>} />
          <Route path="/admin/products/add" element={<AdminLayout><AdminProductForm /></AdminLayout>} />
          <Route path="/admin/products/edit/:id" element={<AdminLayout><AdminProductForm /></AdminLayout>} />
          <Route path="/admin/orders" element={<AdminLayout><AdminOrders /></AdminLayout>} />
          <Route path="/admin/coupons" element={<AdminLayout><AdminCoupons /></AdminLayout>} />
          <Route path="/admin/reports" element={<AdminLayout><AdminReports /></AdminLayout>} />
          <Route path="/admin/management" element={<AdminLayout><ManagementPage /></AdminLayout>} />
          <Route path="/admin/users" element={<AdminLayout><UserManagement /></AdminLayout>} />
          <Route path="/admin/whatsapp" element={<AdminLayout><WhatsAppManagement /></AdminLayout>} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  // Check for app updates every 5 minutes
  useEffect(() => {
    const checkForUpdates = async () => {
      try {
        const response = await fetch('/version.json?t=' + new Date().getTime());
        const data = await response.json();
        
        const currentVersion = localStorage.getItem('app-version');
        
        if (currentVersion && currentVersion !== data.version) {
          console.log('New version detected, reloading...');
          localStorage.setItem('app-version', data.version);
          window.location.reload(true);
        } else if (!currentVersion) {
          localStorage.setItem('app-version', data.version);
        }
      } catch (error) {
        console.error('Failed to check for updates:', error);
      }
    };
    
    // Check immediately on mount
    checkForUpdates();
    
    // Then check every 5 minutes
    const interval = setInterval(checkForUpdates, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
